import { ReactNode, useCallback, useState } from 'react';
import { Button, ButtonProps, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import * as kpiState from '@/modules/KpiTracker/state';
import { LogTemplateType } from '@/__generated__/graphql';

type LogsButtonProps = {
  structureId: string | undefined;
  disabled?: boolean;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  sx?: SxProps<Theme>;
  label?: string;
  id?: string;
  showLogWork?: boolean;
};

export const LogsButton = ({
  structureId,
  disabled = false,
  variant = 'outlined',
  size = 'small',
  startIcon,
  endIcon,
  sx,
  label = 'Logs',
  id,
  showLogWork = false,
}: LogsButtonProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const setSelectedLogTemplateType = useSetAtom(kpiState.selectedLogTemplateType);
  const setKpiPanelOpen = useSetAtom(kpiState.kpiPanelOpen);
  const setKpiPanelStructureIdOverride = useSetAtom(kpiState.kpiPanelStructureIdOverride);
  const selectedWbsFilter = useAtomValue(kpiState.selectedWbsFilter);
  const setKpiPanelWbsFilterSnapshot = useSetAtom(kpiState.kpiPanelWbsFilterSnapshot);
  const setIndirectPobDialogOpen = useSetAtom(kpiState.indirectPobDialogOpen);
  const setIndirectPobDialogStructureId = useSetAtom(kpiState.indirectPobDialogStructureId);
  const setLogWorkDrawerOpen = useSetAtom(kpiState.kpiPlatformWorkpackDrawerOpen);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleLogCost = useCallback(() => {
    handleMenuClose();
    setKpiPanelStructureIdOverride(structureId);
    setKpiPanelWbsFilterSnapshot({ value: selectedWbsFilter });
    setSelectedLogTemplateType(LogTemplateType.CostLogTemplate);
    setKpiPanelOpen(true);
  }, [
    handleMenuClose,
    setKpiPanelStructureIdOverride,
    structureId,
    setKpiPanelWbsFilterSnapshot,
    selectedWbsFilter,
    setSelectedLogTemplateType,
    setKpiPanelOpen,
  ]);

  const handleIndirectPob = useCallback(() => {
    handleMenuClose();
    setIndirectPobDialogStructureId(structureId);
    setIndirectPobDialogOpen(true);
  }, [handleMenuClose, setIndirectPobDialogStructureId, structureId, setIndirectPobDialogOpen]);

  const handleLogWork = useCallback(() => {
    handleMenuClose();
    setLogWorkDrawerOpen(true);
  }, [handleMenuClose, setLogWorkDrawerOpen]);

  return (
    <>
      <Button
        id={id}
        variant={variant}
        size={size}
        onClick={handleMenuOpen}
        disabled={disabled}
        startIcon={startIcon}
        endIcon={endIcon}
        sx={sx}
      >
        {label}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {showLogWork && <MenuItem onClick={handleLogWork}>Log Work</MenuItem>}
        <MenuItem onClick={handleLogCost}>Log Cost</MenuItem>
        <MenuItem onClick={handleIndirectPob} disabled={!structureId}>
          Indirect POB
        </MenuItem>
      </Menu>
    </>
  );
};
